/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2221 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { lineStyle } from "@monsma/App/Components/LineContainer/LineContainer";
import { orderLinesListStyles } from "@monsma/Overrides/Components/OrderLinesList";
import { screen } from "@monsma/XDL/Helpers";
import { labelStyle, valueStyle } from "@monsma/XDL/Utilities/sharedStyles";
import { css } from "styled-components";

interface SharedMyAccountDetailsLines {
    titleText?: TypographyProps;
    smallHeadingAndTextContent?: SmallHeadingAndTextStyles;
    priceSubText?: TypographyProps;
    imageWrapper?: InjectableCss;
    vatLabelText?: TypographyPresentationProps;
    orderLineLabel?: TypographyPresentationProps;
    orderLineText?: TypographyPresentationProps;
}

export const sharedMyAccountDetailsLinesStyles: SharedMyAccountDetailsLines = {
    titleText: {
        variant: "h3",
        color: "secondary",
        css: css`
            margin-bottom: 0;
            padding: 40px 0 20px;
            border-bottom: 1px solid ${getColor("common.ui12")};
            @media print {
                page-break-before: avoid;
                border-width: 2px;
                padding: 10px 0 20px;
            }
        `,
    },
    smallHeadingAndTextContent: {
        text: {
            size: 16,
            lineHeight: "20px",
            weight: "bold",
            color: "common.ui10",
        },
    },
    priceSubText: {
        size: 13,
        lineHeight: "22px",
        weight: "normal",
        css: css`
            margin-left: 5px;
        `,
    },
    imageWrapper: {
        css: css`
            height: 110px;
            ${screen("lte", 460)} {
                width: 100%;
                height: 190px;
            }
        `,
    },
    vatLabelText: {
        variant: "legend",
        css: css`
            margin-top: 10px;
        `,
    },
    orderLineLabel: {
        css: css`
            ${labelStyle};
            line-height: 22px;
        `,
    },
    orderLineText: {
        css: css`
            ${valueStyle};
            line-height: 22px;
        `,
    },
};

orderLinesListStyles.titleText = sharedMyAccountDetailsLinesStyles.titleText;

(orderLinesListStyles as any).orderLineLabel = sharedMyAccountDetailsLinesStyles.orderLineLabel;

(orderLinesListStyles as any).orderLineText = sharedMyAccountDetailsLinesStyles.orderLineText;

(orderLinesListStyles as any).addToListLink = {
    icon: lineStyle.addToListLink?.icon,
    css: css`
        ${lineStyle.addToListLink?.css}
        left: 0;
        right: auto;
    `,
};

orderLinesListStyles.productInfoQtyOrderedHeadingAndText = sharedMyAccountDetailsLinesStyles.smallHeadingAndTextContent;

orderLinesListStyles.productInfoQtyShippedHeadingAndText = {
    ...sharedMyAccountDetailsLinesStyles.smallHeadingAndTextContent,
    wrapper: {
        css: css`
            margin-top: 10px;
            @media print {
                margin-top:0;
                width:100%;
            }
        `,
    },
};

orderLinesListStyles.subtotalWithoutVatHeadingAndText = orderLinesListStyles.productInfoQtyShippedHeadingAndText;

orderLinesListStyles.productInfoSubtotalHeadingAndText = {
    ...orderLinesListStyles.productInfoQtyShippedHeadingAndText,
    wrapper: {
        css: css`
            @media print {
                width: auto;
                flex-grow: 1;
            }
        `,
    },
};

orderLinesListStyles.subtotalWithoutVatHeadingAndText = orderLinesListStyles.productInfoQtyShippedHeadingAndText;

(orderLinesListStyles as any).priceWrapper = {
    css: css`
        @media print {
            flex-grow: 1;
        }
    `,
};

(orderLinesListStyles as any).orderLineInfoPromotionWrapper = {
    css: css`
        margin-top: 10px;
    `,
};

(orderLinesListStyles as any).priceHeading = {
    variant: "legend",
};

(orderLinesListStyles as any).priceText = {
    ...sharedMyAccountDetailsLinesStyles.smallHeadingAndTextContent?.text,
};

(orderLinesListStyles as any).priceSubText = sharedMyAccountDetailsLinesStyles.priceSubText;

(orderLinesListStyles as any).imageWrapper = sharedMyAccountDetailsLinesStyles.imageWrapper;

orderLinesListStyles.vatLabelText = sharedMyAccountDetailsLinesStyles.vatLabelText;
